import { SubmissionStatus } from '@bp/bp-graphql-types';
import { Button, Chip, DotsHorizontalIcon, Dropdown, DropdownMenu, LazyLoader } from '@bp/ui-components';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { BpLink } from 'components/BpLink/BpLink';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useDays } from 'hooks/useDays';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'utils/dimensions';
import { useRooms } from '../../../../matrix/hooks/useRooms';
import styles from './AssignmentCard.module.scss';

type AssignmentCardTeacherProps = {
  assignmentUuid: string;
  loading: boolean;
  onNavigate: (uuid: string) => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string) => void;
};

export const AssignmentCardTeacher = ({
  assignmentUuid,
  loading,
  onNavigate,
  onEdit,
  onDelete,
}: AssignmentCardTeacherProps) => {
  const { t } = useTranslation();
  const pimAuthClaims = useAuthClaims().pimAuthClaims;
  const { courseUuid } = useRequireParams(['courseUuid']);
  const rooms = useRooms();

  const { daysLeft } = useDays();
  const windowWidth = useWindowDimensions().width;

  const permissions = usePermissionChecker();

  const { assignments } = useAssignments({ courseUuid, assignmentUuid });

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  if (!assignment) throw new Error('Assignment not found');

  const { uuid, title, dueDate, submissions } = assignment;

  const daysDifference = daysLeft(dueDate.toISOString(), true);

  const newSubmissionsCount = submissions.filter((s) => s.status === SubmissionStatus.New).length;

  const openSubmissions = submissions.filter(
    (s) => s.status !== SubmissionStatus.New && s.status !== SubmissionStatus.Done,
  ).length;

  const chipText: string =
    windowWidth >= 1250
      ? t('submissions.newSubmissions', { count: newSubmissionsCount })
      : newSubmissionsCount.toString();

  const newMessages = assignment?.submissions.some(
    (submission) =>
      rooms?.find((room) => room.name.includes(submission.uuid))?.getRoomUnreadNotificationCount() ?? 0 > 0,
  );

  const canUpdate = permissions?.canUpdateAssignment({
    uuid: uuid,
    ownerUuid: pimAuthClaims.getProfile().uuid,
    groupUuid: courseUuid ?? '',
    organization: { uuid: pimAuthClaims.getOrganizationUuid() },
  });

  return (
    <div className={styles['assignment-card']}>
      {loading && <LazyLoader embedded className={styles.loader} />}
      <div className={styles.top}>
        <div className={styles.count}>
          {daysDifference < 0
            ? t('assignments.overdue')
            : t('dates.daysToGo', {
                count: daysDifference,
              })}
        </div>
        <div className={styles.actions}>
          <Dropdown trigger={<Button hierarchy='ghost' icon={<DotsHorizontalIcon className='small' />} />}>
            <DropdownMenu
              data={[
                {
                  label: t('common.edit'),
                  onClick: () => onEdit(uuid),
                  disabled: !canUpdate,
                },

                {
                  type: 'ruler',
                },

                {
                  label: t('delete.delete'),
                  color: 'error',
                  onClick: () => onDelete(uuid),
                  disabled: !canUpdate,
                },
              ]}
            />
          </Dropdown>
        </div>
      </div>

      <BpLink value={title} onNavigate={() => onNavigate(uuid)} isEditorOfCourse className={styles.link} />

      <div className={styles.bottom}>
        <div className={styles.info}>
          <Chip value={chipText} />
          {newMessages && <div className={'bp__dot'} />}
        </div>
        <div className={styles.open}>
          <div className={styles.text}>{t('common.open')}</div>
          <div className={styles.count}>{openSubmissions}</div>
        </div>
      </div>
    </div>
  );
};
